import { FastifyReply, FastifyRequest } from "fastify";
import { GameData } from "gamedata/GameData";
import { ReactNode, useEffect } from "react";
import { ShouldRevalidateFunction, useLoaderData } from "react-router";
import { ZodSchema } from "zod";
import { Realm, lstring, useRealm, useT } from "./Lang";
import { Layout } from "~/components/Layout";

export async function clientLoader(args: OurLoaderArgs) {
  const url = new URL(args.url);
  url.searchParams.set("_data", "");
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data && data["__rr_response"]) {
      const res = data["__rr_response"];
      return new Response("", res.init);
    }
    return data;
  } catch (e) {
    console.log("Catch", e);
    return null;
  }
}

export type OurLoaderArgs = {
  url: URL;
  realm: Realm;
  gd: GameData;
  params: unknown;
  searchParams: URLSearchParams;
  req: FastifyRequest;
  res: FastifyReply;
  setStatusCode: (status: number) => void;
};
export type OurLoader = (args: OurLoaderArgs) => unknown;

export type OurRoute = {
  path: string;
  element: ReactNode;
  loader?: OurLoader;
  shouldRevalidate?: ShouldRevalidateFunction;
  meta?: MetaFunc<any>;
};

export type MetaFunc<T> = (a: MetaArgs<T>) => MetaInfo;
export type MetaInfo = {
  title: string;
  description?: string | null;
  canonicalUrl?: string;
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogType?: "article" | null;
  ogUrl?: string | null;
  ogImage?: string | null;
};
export type MetaArgs<T> = {
  data: T;
  realm: Realm;
  t: (str: lstring) => string;
};

export type Loader<T> = (args: OurLoaderArgs) => Promise<Response | T>;
export function createLoader<T>(f: Loader<T>): Loader<T> {
  return f;
}

export type GetLoaderData<L extends Loader<any>> =
  L extends Loader<infer T> ? T : never;

export function Page(props: {
  meta?: MetaFunc<any>;
  children: ReactNode;
  user?: { id: number };
}) {
  const data = useLoaderData() as Record<string, any>;
  const realm = useRealm();
  const t = useT();
  const meta = props.meta?.({ data, t, realm });
  const title = meta?.title ?? "L2hub";
  useEffect(() => {
    if (document.title !== title) {
      document.title = title;
    }
  }, [title]);

  return (
    <Layout realm={realm} authUser={data.authUser}>
      {props.children}
    </Layout>
  );
}

export type PageOpts<T> = {
  path: string;
  loader?: { loader_impl: Loader<T> } | undefined;
  shouldRevalidate?: ShouldRevalidateFunction;
  meta?: MetaFunc<T>;
  render: (props: { data: T }) => ReactNode;
};

export const GLOBAL_ROUTES: OurRoute[] = [];

export function $PAGE<T>(opts: PageOpts<T>) {
  function LoadData() {
    const data = useLoaderData() as T;
    return opts.render({ data });
  }
  GLOBAL_ROUTES.push({
    path: opts.path,
    element: <LoadData />,
    loader: opts.loader?.loader_impl ?? clientLoader,
    shouldRevalidate: opts.shouldRevalidate,
    meta: opts.meta,
  });
}

function makeLoader<T>(f: Loader<T>) {
  return { loader_impl: f };
}

export const $LOADER = SRV ? makeLoader : undefined;

export type ServerOnly<T> = T | undefined;

export const $SERVER = SRV
  ? function <T>(v: T): T {
      return v;
    }
  : undefined;

export type ServerFnArgs<I> = {
  realm: Realm;
  gd: GameData;
  input: I;
  req: FastifyRequest;
  res: FastifyReply;
};
export type ServerFn<I, R> = (args: ServerFnArgs<I>) => Promise<R>;

export type ApiMeta<I> = {
  path: string;
  method: "POST" | "GET";
  parser: ZodSchema<I>;
};

export type ApiInfo<I, R> = ApiMeta<I> & {
  serverFn: ServerOnly<ServerFn<I, R>>;
};

export const GLOBAL_APIS: ApiInfo<any, unknown>[] = [];

export function $API<I>(meta: ApiMeta<I>) {
  return function <R>(serverFn: ServerOnly<ServerFn<I, R>>): ApiInfo<I, R> {
    const api = {
      ...meta,
      serverFn,
    };
    if (SRV) {
      GLOBAL_APIS.push(api);
    }
    return api;
  };
}

type ApiPayload<I> = {
  realm: Realm;
  input: I;
};

export function callApi<I, R>(
  api: ApiInfo<I, R>,
  opts: { input: I; realm: Realm } & RequestInit,
): Promise<R> {
  const { input, realm, ...init } = opts;

  if (init.method == undefined) {
    init.method = api.method;
  }

  const payload: ApiPayload<I> = { input, realm };
  const payloadJson = JSON.stringify(payload);

  const url = new URL("/" + api.path, location.href);
  if (api.method === "GET" || init.body) {
    url.searchParams.append("__api_payload", payloadJson);
  } else {
    let formData = new FormData();
    formData.append("__api_payload", payloadJson);
    init.body = formData;
  }

  return fetch(url, init).then((res) => res.json());
}
